/* assets/littleg-geo-game-bridge.js — MAPBOX ASSEMBLY GEO GAME BRIDGE (V6)
 * Updated: 2026-09-21
 * Shared optional backend bridge for LittleG geo rounds & Spatial AI missions.
 *
 * RESPONSIBILITY BOUNDARY (0 Conflict / 0 Overlap Contract)
 * - Map Canvas & Marker UI Owner: assets/littleg-kid-map.js
 * - Family Center Account Owner: assets/littleg-family-account.js (FAMILY-IDENTITY-PRIVACY-07)
 * - Family Location Check Owner: assets/littleg-family-address-check.js (V03)
 * - Device Profile Identity Owner: assets/littleg-profile.js (V1.1.0)
 * - THIS FILE OWNS EXCLUSIVELY:
 *   1. Endpoint safety validation & Same-Origin / GCITY API routing.
 *   2. Session proof caching (SessionStorage + memory fallback).
 *   3. Transport normalization for Geo Mission Start/Guess JSON payloads.
 *   4. Feature Guard enforcement for P0 Lite GuessMap policies.
 *
 * NON-OWNERSHIP GUARANTEE:
 * No GFun mutation, no customer checkout, no paid Places API calls, no third-party domain leakage,
 * no child profile modification, and no direct Mapbox DOM marker manipulation.
 */
(function () {
  'use strict';

  var VERSION = '2026-09-21-mapbox-geo-bridge-v6';
  if (window.LittleGGeoGameBridge && window.LittleGGeoGameBridge.version === VERSION) return;

  var OLD_ROOT = '[data-littleg-city-mission-engine]';
  var DAILY_ROOT = '[data-littleg-daily-mission-flow]';
  var KEY_PREFIX = 'littleg.geo.round.';
  var memoryProof = {};

  function all(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function one(root, selector) {
    return root && root.querySelector ? root.querySelector(selector) : null;
  }

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function setText(node, value) {
    if (node && value !== undefined && value !== null) node.textContent = String(value);
  }

  function emit(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
    } catch (_) {}
  }

  function nowIso() {
    try {
      return new Date().toISOString();
    } catch (_) {
      return '';
    }
  }

  function arr(value) {
    return Array.isArray(value) ? value : [];
  }

  function str(value) {
    return value === undefined || value === null ? '' : String(value);
  }

  /* 端點安全校驗：只允許 Same-Origin 及 GCITY API 白名單 */
  function allowedUrl(value) {
    value = clean(value);
    if (!value) return '';

    var routes = window.GCITY_ROUTES;
    var resolved = routes && typeof routes.resolve === 'function' ? routes.resolve(value) : value;

    try {
      var url = new URL(resolved, window.location.origin);
      var apiBase = String((routes && routes.apiBase) || '/apps/gcity-api').replace(/\/+$/, '');

      if (url.origin !== window.location.origin) return '';
      if (url.pathname !== apiBase && url.pathname.indexOf(apiBase + '/') !== 0) return '';

      return url.toString();
    } catch (_) {
      return '';
    }
  }

  function readJson(response) {
    return response.text().then(function (text) {
      var json = {};
      if (text) {
        try {
          json = JSON.parse(text);
        } catch (_) {
          json = { raw: text };
        }
      }
      if (!response.ok) {
        var error = new Error('geo_bridge_http_' + response.status);
        error.payload = json;
        throw error;
      }
      return json;
    });
  }

  function postJson(url, payload, extraHeaders) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: Object.assign(
        { 'Content-Type': 'application/json', Accept: 'application/json' },
        extraHeaders || {}
      ),
      body: JSON.stringify(payload || {}),
    }).then(readJson);
  }

  function dataOf(json) {
    return json && json.data ? json.data : json || {};
  }

  function rootContextPolicy(root) {
    root = root || {};
    var dataset = root.dataset || {};
    return {
      location_bank_mode: dataset.locationBankMode || 'static-internal-p0',
      location_bank_version: dataset.locationBankVersion || 'hk-daily-mission-bank-01',
      scene_fallback_mode: dataset.sceneFallbackMode || 'visual-class-static-fallback',
      source_policy: dataset.freeOpenSourcePolicy || 'free-no-registration-no-api-key',
      geo_guessmap_mode: dataset.geoGuessmapMode || 'disabled-p0-lite',
      geo_guessmap_owner: dataset.geoGuessmapOwner || 'feature-guard-only',
      geo_guessmap_render: dataset.geoGuessmapRender || 'disabled',
      geo_guessmap_policy: dataset.geoGuessmapPolicy || 'no-map-sdk-no-paid-api',
    };
  }

  function applyGuessMapGuard(root) {
    if (!root || !root.dataset) return;
    var policy = rootContextPolicy(root);
    root.dataset.geoGuessmapMode = policy.geo_guessmap_mode;
    root.dataset.geoGuessmapOwner = policy.geo_guessmap_owner;
    root.dataset.geoGuessmapRender = 'disabled';
    root.dataset.geoGuessmapStatus = 'disabled-p0-lite';
    root.dataset.geoGuessmapPolicy = policy.geo_guessmap_policy;
  }

  function unpackStart(json) {
    var data = dataOf(json);
    return {
      ok: data.ok !== false,
      raw: json,
      round_id: str(data.round_id || data.roundId || data.id),
      round_proof: str(
        data.round_proof || data.roundProof || data.session_token || data.sessionToken
      ),
      round_key: str(data.round_key || data.roundKey || data.key),
      scene_handle: str(data.scene_handle || data.sceneHandle || data.scene),
      hero_type: str(data.hero_type || data.heroType || data.hero),
      hero_label: str(data.hero_label || data.heroType || data.hero_copy || data.heroCopy),
      prompt: str(data.prompt || data.title || data.mission_title || data.missionTitle),
      clue_ids: arr(data.clue_ids || data.clueIds),
      clue_hints: arr(data.clue_hints || data.clueHints || data.clues),
      clue_reasons: arr(data.clue_reasons || data.clueReasons || data.reasons),
      sourceRole: str(data.sourceRole || data.source_role),
      sourceId: str(data.sourceId || data.source_id),
      apiMode: str(data.apiMode || data.api_mode),
      attributionNote: str(data.attributionNote || data.attribution_note),
      expires_at: str(data.expires_at || data.expiresAt),
      location_context: data.location_context || data.locationContext || null,
      weather_context: data.weather_context || data.weatherContext || null,
      poi_context: data.poi_context || data.poiContext || null,
      mission_context: data.mission_context || data.missionContext || null,
      map_context: data.map_context || data.mapContext || null,
      source_policy: data.source_policy || data.sourcePolicy || null,
      geo_guessmap_guard: data.geo_guessmap_guard || data.geoGuessmapGuard || null,
    };
  }

  function unpackGuess(json) {
    var data = dataOf(json);
    return {
      ok: data.ok !== false,
      raw: json,
      resultId: str(data.result_id || data.resultId || data.id),
      roundId: str(data.round_id || data.roundId),
      score: data.score !== undefined ? data.score : null,
      distanceM:
        data.distance_m !== undefined
          ? data.distance_m
          : data.distanceM !== undefined
            ? data.distanceM
            : data.distance_meters !== undefined
              ? data.distance_meters
              : null,
      distanceLabel: str(data.distance_label || data.distanceLabel),
      resultStatus: str(data.result_status || data.resultStatus),
      sourceRole: str(data.sourceRole || data.source_role),
      sourceId: str(data.sourceId || data.source_id),
      apiMode: str(data.apiMode || data.api_mode),
      attributionNote: str(data.attributionNote || data.attribution_note),
    };
  }

  function storeProof(id, proof) {
    id = str(id);
    proof = str(proof);
    if (!id || !proof) return;
    memoryProof[id] = proof;
    try {
      sessionStorage.setItem(KEY_PREFIX + id, proof);
    } catch (_) {}
  }

  function getProof(id) {
    id = str(id);
    if (!id) return '';
    if (memoryProof[id]) return memoryProof[id];
    try {
      return sessionStorage.getItem(KEY_PREFIX + id) || '';
    } catch (_) {
      return '';
    }
  }

  function endpoint(root, type) {
    if (!root || !root.dataset) return '';
    if (type === 'start')
      return allowedUrl(root.dataset.geoGameStartUrl || root.dataset.geoGameUrl || '');
    if (type === 'guess') return allowedUrl(root.dataset.geoGameGuessUrl || '');
    return '';
  }

  function startPayload(root) {
    return Object.assign(
      {
        source: 'littleg-city-mission-engine',
        mode: 'optional-backend-bridge',
        city_pack_id: root.dataset.cityPackId || 'hk',
        city: root.dataset.city || 'hong-kong',
        city_label: root.dataset.cityLabel || 'Hong Kong',
        district: root.dataset.district || root.dataset.defaultDistrict || 'hong-kong',
        district_label: root.dataset.districtLabel || '',
        scene_handle: root.dataset.defaultScene || root.dataset.serviceLane || 'urban-renewal',
        round_key: root.dataset.engineRoundKey || '',
        locale:
          root.dataset.cityLocale ||
          root.dataset.locale ||
          document.documentElement.lang ||
          'zh-TW',
        client_time: nowIso(),
      },
      rootContextPolicy(root)
    );
  }

  function startPayloadFromMission(root, mission) {
    mission = mission || {};
    return Object.assign(
      {
        source: 'littleg-daily-mission-flow',
        intent: 'immersive-mission',
        city_pack_id: root.dataset.cityPackId || 'hk',
        city: root.dataset.city || 'hong-kong',
        city_label: root.dataset.cityLabel || 'Hong Kong',
        district: root.dataset.district || 'hong-kong',
        district_label: root.dataset.districtLabel || root.dataset.district || 'Hong Kong',
        scene_handle:
          mission.sceneKey || mission.scene_handle || root.dataset.defaultScene || 'urban-renewal',
        round_key: mission.roundKey || mission.round_key || '',
        hero_type: mission.heroType || mission.hero || mission.hero_type || '',
        locale:
          root.dataset.cityLocale ||
          root.dataset.locale ||
          document.documentElement.lang ||
          'zh-TW',
        client_time: nowIso(),
        card_seed: {
          hero: mission.hero || '',
          prompt: mission.prompt || '',
          sourceRole: mission.sourceRole || '',
          sourceId: mission.sourceId || '',
          apiMode: mission.apiMode || '',
          location: mission.location || '',
          time: mission.time || '',
          impact: mission.impact || '',
        },
      },
      rootContextPolicy(root)
    );
  }

  function applyStartToRoot(root, round, source) {
    if (!root || !round) return;
    root.dataset.geoBridgeStatus = 'round-ready';
    root.dataset.geoBackendMode = source || 'optional';
    root.dataset.geoRoundId = round.round_id || '';
    if (round.scene_handle) root.dataset.geoSceneHandle = round.scene_handle;
    if (round.round_key) root.dataset.geoRoundKey = round.round_key;
    if (round.location_context) root.dataset.geoLocationContext = 'ready';
    if (round.weather_context) root.dataset.geoWeatherContext = 'ready';
    if (round.poi_context) root.dataset.geoPoiContext = 'ready';
    if (round.mission_context) root.dataset.geoMissionContext = 'ready';
    if (round.source_policy) root.dataset.geoSourcePolicy = 'ready';
    applyGuessMapGuard(root);
  }

  function startFromMission(root, mission) {
    var url = endpoint(root, 'start');
    if (!url) {
      if (root) {
        root.dataset.geoBridgeStatus = 'disabled';
        applyGuessMapGuard(root);
      }
      return Promise.resolve({ ok: false, status: 'disabled', mode: 'local-only' });
    }
    if (root && root.__lgGeoStartInFlight)
      return Promise.resolve({ ok: false, status: 'in-flight' });
    if (root) {
      root.__lgGeoStartInFlight = true;
      root.dataset.geoBridgeStatus = 'start-loading';
      applyGuessMapGuard(root);
    }

    return postJson(url, startPayloadFromMission(root, mission))
      .then(function (json) {
        var round = unpackStart(json);
        if (!round.round_id) throw new Error('geo_bridge_missing_round_id');
        storeProof(round.round_id, round.round_proof);
        applyStartToRoot(root, round, 'daily-flow');

        emit('littleg:geo-game:round-ready', {
          root_id: (root && root.id) || '',
          round_id: round.round_id,
          round_key: round.round_key,
          scene_handle: round.scene_handle,
          hero_type: round.hero_type,
          prompt: round.prompt,
          clue_hints: round.clue_hints,
          clue_reasons: round.clue_reasons,
          clue_ids: round.clue_ids,
          location_context: round.location_context,
          weather_context: round.weather_context,
          poi_context: round.poi_context,
          mission_context: round.mission_context,
          map_context: round.map_context,
          source_policy: round.source_policy,
          geo_guessmap_guard: round.geo_guessmap_guard || rootContextPolicy(root),
          source: 'backend-daily-flow',
        });
        return round;
      })
      .catch(function (error) {
        if (root) {
          root.dataset.geoBridgeStatus = 'fallback-local';
          root.dataset.geoBridgeError = String((error && error.message) || error || 'unknown');
          applyGuessMapGuard(root);
        }
        return { ok: false, status: 'fallback-local', error: error };
      })
      .finally(function () {
        if (root) root.__lgGeoStartInFlight = false;
      });
  }

  function startRound(root) {
    var url = endpoint(root, 'start');
    if (!url) {
      root.dataset.geoBridgeStatus = 'disabled';
      applyGuessMapGuard(root);
      return;
    }
    if (root.__lgGeoStartInFlight) return;

    root.__lgGeoStartInFlight = true;
    root.dataset.geoBridgeStatus = 'start-loading';
    applyGuessMapGuard(root);

    postJson(url, startPayload(root))
      .then(function (json) {
        var round = unpackStart(json);
        if (!round.round_id) throw new Error('geo_bridge_missing_round_id');
        storeProof(round.round_id, round.round_proof);
        applyStartToRoot(root, round, 'optional');

        emit('littleg:geo-game:round-ready', {
          root_id: root.id || '',
          round_id: round.round_id,
          round_key: round.round_key,
          scene_handle: round.scene_handle,
          hero_type: round.hero_type,
          prompt: round.prompt,
          clue_hints: round.clue_hints,
          clue_reasons: round.clue_reasons,
          clue_ids: round.clue_ids,
          location_context: round.location_context,
          weather_context: round.weather_context,
          poi_context: round.poi_context,
          mission_context: round.mission_context,
          map_context: round.map_context,
          source_policy: round.source_policy,
          geo_guessmap_guard: round.geo_guessmap_guard || rootContextPolicy(root),
          source: 'backend-optional',
        });
      })
      .catch(function (error) {
        root.dataset.geoBridgeStatus = 'fallback-local';
        root.dataset.geoBridgeError = String((error && error.message) || error || 'unknown');
        applyGuessMapGuard(root);
      })
      .finally(function () {
        root.__lgGeoStartInFlight = false;
      });
  }

  function buildGuessPayload(root, resultPayload) {
    resultPayload = resultPayload || {};
    var roundId = resultPayload.roundId || resultPayload.round_id || root.dataset.geoRoundId || '';
    return Object.assign(
      {
        source: resultPayload.source || 'littleg-daily-mission-flow',
        round_id: roundId,
        round_proof: resultPayload.roundProof || resultPayload.round_proof || getProof(roundId),
        scene_handle: resultPayload.sceneKey || resultPayload.scene_handle || '',
        round_key: resultPayload.roundKey || resultPayload.round_key || '',
        hero_type: resultPayload.heroType || resultPayload.hero || resultPayload.hero_type || '',
        selected_clue_ids: arr(
          resultPayload.selectedClueIds || resultPayload.selected_clue_ids || resultPayload.clue_ids
        ),
        selected_clue_labels: arr(
          resultPayload.selectedClueLabels ||
            resultPayload.selected_clue_labels ||
            resultPayload.clues ||
            resultPayload.clue_labels
        ),
        user_observation:
          resultPayload.userObservation ||
          resultPayload.user_observation ||
          resultPayload.observation ||
          '',
        locale:
          root.dataset.cityLocale ||
          root.dataset.locale ||
          document.documentElement.lang ||
          'zh-TW',
        location: {
          city: resultPayload.city || root.dataset.city || 'hong-kong',
          city_label: resultPayload.cityLabel || root.dataset.cityLabel || 'Hong Kong',
          district: resultPayload.district || root.dataset.district || 'hong-kong',
          district_label: resultPayload.districtLabel || root.dataset.districtLabel || 'Hong Kong',
        },
        card_context: {
          hero: resultPayload.hero || '',
          impact: resultPayload.impact || '',
          location: resultPayload.location || '',
          time: resultPayload.time || '',
        },
        location_context: resultPayload.locationContext || resultPayload.location_context || null,
        weather_context: resultPayload.weatherContext || resultPayload.weather_context || null,
        poi_context: resultPayload.poiContext || resultPayload.poi_context || null,
        mission_context: resultPayload.missionContext || resultPayload.mission_context || null,
      },
      rootContextPolicy(root)
    );
  }

  function submitFromDailyResult(root, resultPayload) {
    var url = endpoint(root, 'guess');
    if (!url) {
      if (root) {
        root.dataset.geoGuessStatus = 'disabled';
        applyGuessMapGuard(root);
      }
      return Promise.resolve({ ok: false, status: 'disabled', mode: 'local-only' });
    }
    var payload = buildGuessPayload(root, resultPayload);
    if (!payload.round_id) {
      if (root) {
        root.dataset.geoGuessStatus = 'missing-round';
        applyGuessMapGuard(root);
      }
      return Promise.resolve({ ok: false, status: 'missing-round' });
    }
    if (root && root.__lgGeoGuessInFlight)
      return Promise.resolve({ ok: false, status: 'in-flight' });
    if (root) {
      root.__lgGeoGuessInFlight = true;
      root.dataset.geoGuessStatus = 'submitting';
      applyGuessMapGuard(root);
    }

    return postJson(url, payload)
      .then(function (json) {
        var result = unpackGuess(json);
        if (root) {
          root.dataset.geoGuessStatus = 'scored';
          applyGuessMapGuard(root);
        }
        emit('littleg:geo-game:scored', {
          root_id: (root && root.id) || '',
          result_id: result.resultId,
          round_id: result.roundId,
          score: result.score,
          distance_m: result.distanceM,
          distance_label: result.distanceLabel,
          result_status: result.resultStatus,
        });
        return result;
      })
      .catch(function (error) {
        if (root) {
          root.dataset.geoGuessStatus = 'fallback-local';
          root.dataset.geoGuessError = String((error && error.message) || error || 'unknown');
          applyGuessMapGuard(root);
        }
        return { ok: false, status: 'fallback-local', error: error };
      })
      .finally(function () {
        if (root) root.__lgGeoGuessInFlight = false;
      });
  }

  function findRoot(card) {
    var id = card && card.id ? String(card.id) : '';
    if (id) {
      var escaped = window.CSS && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '\\"');
      var found = document.querySelector(OLD_ROOT + '[data-last-card-id="' + escaped + '"]');
      if (found) return found;
    }
    var roots = all(document, OLD_ROOT);
    return roots.length === 1 ? roots[0] : null;
  }

  function value(json, key) {
    var data = dataOf(json);
    return data[key] !== undefined
      ? data[key]
      : json && json[key] !== undefined
        ? json[key]
        : undefined;
  }

  function showResult(root, json) {
    var score = value(json, 'score');
    var distance = value(json, 'distance_meters');
    if (distance === undefined) distance = value(json, 'distance_m');
    if (distance === undefined) distance = value(json, 'distance');

    var note = one(root, '[data-geo-result-note]');
    if (note) {
      note.hidden = false;
      note.setAttribute('aria-hidden', 'false');
    }

    if (score !== undefined && score !== null && score !== '') {
      root.dataset.geoGuessScore = String(score);
      setText(one(root, '[data-geo-score]'), 'Score ' + score);
    }
    if (distance !== undefined && distance !== null && distance !== '') {
      var number = Number(distance);
      var copy = Number.isFinite(number)
        ? number >= 1000
          ? Math.round(number / 100) / 10 + ' km'
          : Math.round(number) + ' m'
        : String(distance);
      root.dataset.geoGuessDistance = copy;
      setText(one(root, '[data-geo-distance]'), 'Distance ' + copy);
    }
  }

  function submitGuess(detail) {
    var card = detail && detail.card ? detail.card : null;
    var root = findRoot(card);
    if (!(root instanceof HTMLElement)) return;

    var url = endpoint(root, 'guess');
    var roundId = root.dataset.geoRoundId || (card && card.geo_round_id) || '';
    if (!url || !roundId) {
      root.dataset.geoGuessStatus = 'disabled';
      applyGuessMapGuard(root);
      return;
    }
    if (root.__lgGeoGuessInFlight) return;

    root.__lgGeoGuessInFlight = true;
    root.dataset.geoGuessStatus = 'submitting';
    applyGuessMapGuard(root);

    var proof = getProof(roundId);
    postJson(
      url,
      Object.assign(
        {
          round_id: roundId,
          round_proof: proof,
          card_id: (card && card.id) || '',
          clue_ids: card && Array.isArray(card.clue_ids) ? card.clue_ids : [],
          clue_labels: card && Array.isArray(card.clue_labels) ? card.clue_labels : [],
          observation: (card && card.observation) || '',
          city: (card && card.city) || root.dataset.city || 'hong-kong',
          district: (card && card.district) || root.dataset.district || 'hong-kong',
          locale: 'en',
          source: 'littleg-city-mission-engine',
        },
        rootContextPolicy(root)
      )
    )
      .then(function (json) {
        root.dataset.geoGuessStatus = 'scored';
        showResult(root, json);
        applyGuessMapGuard(root);
      })
      .catch(function (error) {
        root.dataset.geoGuessStatus = 'fallback-local';
        root.dataset.geoGuessError = String((error && error.message) || error || 'unknown');
        applyGuessMapGuard(root);
      })
      .finally(function () {
        root.__lgGeoGuessInFlight = false;
      });
  }

  function initOld(root) {
    if (!root || root.__lgGeoBridgeBound) return;
    root.__lgGeoBridgeBound = true;
    root.dataset.geoBridgeVersion = VERSION;
    applyGuessMapGuard(root);
    startRound(root);
  }

  function initDaily(root) {
    if (!root || root.__lgGeoBridgeDailyBound) return;
    root.__lgGeoBridgeDailyBound = true;
    root.dataset.geoBridgeVersion = VERSION;
    applyGuessMapGuard(root);
    if (!endpoint(root, 'start')) root.dataset.geoBridgeStatus = 'disabled';
  }

  function boot() {
    all(document, OLD_ROOT).forEach(initOld);
    all(document, DAILY_ROOT).forEach(initDaily);
  }

  // Shopify Section Unload 記憶體釋放與腳本解綁
  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event.target || document;
    all(scope, OLD_ROOT)
      .concat(all(scope, DAILY_ROOT))
      .forEach(function (root) {
        root.__lgGeoBridgeBound = false;
        root.__lgGeoBridgeDailyBound = false;
        root.__lgGeoStartInFlight = false;
        root.__lgGeoGuessInFlight = false;
      });
  });

  window.LittleGGeoGameBridge = Object.freeze({
    version: VERSION,
    allowedUrl: allowedUrl,
    startFromMission: startFromMission,
    submitFromDailyResult: submitFromDailyResult,
    storeProof: storeProof,
    getProof: getProof,
  });

  window.addEventListener('littleg:mission_complete', function (event) {
    submitGuess(event.detail || {});
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
